import { useMemo } from 'react';

import type { FeatureToggleDto } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { getFeatureToggles, FeatureToggleName } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useRequestLegacy } from '@/js/libs/use-request-legacy';

import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';

type FeaturesMap = {
  [value in FeatureToggleName]?: boolean;
};

const DEFAULT_FEATURE_TOGGLE_STATE: FeatureToggleDto[] = [];

const { useFeatureToggleContext } = createContexts({
  FeatureToggle() {
    const [featureList, { pending }] = useRequestLegacy({
      apiMethod: getFeatureToggles,
      defaultData: DEFAULT_FEATURE_TOGGLE_STATE,
      onFail: useRequestErrorNotification(),
    });

    const featuresMap = useMemo(
      () =>
        [...featureList].reduce<FeaturesMap>((acc, feature) => {
          if (!feature.name || !feature.enabled) {
            return acc;
          }

          return {
            ...acc,
            [feature.name]: feature.enabled,
          };
        }, {}),
      [featureList],
    );

    const isManualPostInvoicingEnabled = featuresMap[FeatureToggleName.MANUAL_POST_INVOICING];
    const isScreenRestrictionsEnabled = featuresMap[FeatureToggleName.SCREEN_RESTRICTIONS];
    const isAccountingNumberEnabled = featuresMap[FeatureToggleName.ACCOUNTING_NUMBER];
    const isPositionOverwritingEnabled = featuresMap[FeatureToggleName.POSITION_OVERWRITING];
    const isBriefingEmlEnabled = featuresMap[FeatureToggleName.BRIEFING_EML];
    const isDefaultServiceProductDiscountEnabled = featuresMap[FeatureToggleName.DEFAULT_SERVICE_PRODUCT_DISCOUNT];
    const isDefaultOnlineLineItemsMaxCapacityEnabled = featuresMap[FeatureToggleName.DEFAULT_ONLINE_LINE_ITEMS_MAX_CAPACITY];
    const isReachEnabled = featuresMap[FeatureToggleName.REACH];
    const isOnlineOverbookingLimitEnabled = featuresMap[FeatureToggleName.ONLINE_POSITIONS_OVERBOOKING_VALIDATION];
    const isExistingCustomersReductionEnabled = featuresMap[FeatureToggleName.EXISTING_CUSTOMERS];
    const isPostronMobileInAppDisplaySuppressionEnabled = featuresMap[FeatureToggleName.POSTRON_MOBILE_IN_APP_DISPLAY_SUPPRESSION];
    const isPostronMobileInAppDisplayHidingEnabled = featuresMap[FeatureToggleName.POSTRON_MOBILE_IN_APP_DISPLAY_HIDING];
    const isDynamicCampaignManagementGrossEnabled = featuresMap[FeatureToggleName.DYNAMIC_CAMPAIGN_MANAGEMENT_GROSS];
    const isPathApiEnabled = featuresMap[FeatureToggleName.PATH_API];
    const isDynamicDoohPositionsRenameEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS_RENAME];
    const isDynamicDoohPositionsEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS];
    const isDigitalPositionsCloneEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS_CLONE];
    const isDataProductPositionsEnabled = featuresMap[FeatureToggleName.DATA_PRODUCT_POSITIONS];

    return {
      pending,
      isManualPostInvoicingEnabled,
      isScreenRestrictionsEnabled,
      isAccountingNumberEnabled,
      isPositionOverwritingEnabled,
      isBriefingEmlEnabled,
      isDefaultServiceProductDiscountEnabled,
      isDefaultOnlineLineItemsMaxCapacityEnabled,
      isReachEnabled,
      isOnlineOverbookingLimitEnabled,
      isExistingCustomersReductionEnabled,
      isMobileInAppDisabled: isPostronMobileInAppDisplaySuppressionEnabled,
      isMobileInAppDisabledView: isPostronMobileInAppDisplayHidingEnabled,
      isDynamicCampaignManagementGrossEnabled,
      isPathApiEnabled,
      isDynamicDoohPositionsRenameEnabled,
      isDynamicDoohPositionsEnabled,
      isDigitalPositionsCloneEnabled,
      isDataProductPositionsEnabled,
    };
  },
});

export { useFeatureToggleContext };
